import React from 'react';
import DailyResultsChart from './DailyResultsChart';
import DashboardPanel from './DashboardPanel';
import { projectName, qualityCopy } from './qualityOverviewCopy';
import { historyCopy } from './historicalAnalyticsCopy';

const rate = (value) => (value === null ? '—' : `${value}%`);
const delta = (value, suffix = '') =>
  value === null ? '—' : `${value > 0 ? '+' : ''}${value}${suffix}`;

export default function HistoricalAnalytics({ data, language, onProject, onOpen, opening }) {
  const copy = historyCopy(language);
  const base = qualityCopy(language);
  const history = data.history;
  const comparison = history.comparison;
  return (
    <div className="space-y-6" role="tabpanel" aria-label={copy.title}>
      <div>
        <h3 className="text-xl font-semibold text-slate-900">{copy.title}</h3>
        <p className="text-sm text-slate-500 mt-1">{copy.hint}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          [
            copy.executions,
            comparison.current.completed,
            comparison.previous.completed,
            delta(comparison.execution_delta),
          ],
          [
            copy.success,
            rate(comparison.current.success_rate),
            rate(comparison.previous.success_rate),
            delta(comparison.success_rate_delta_pp, ' pp'),
          ],
          [
            copy.failures,
            comparison.current.failed,
            comparison.previous.failed,
            delta(comparison.current.failed - comparison.previous.failed),
          ],
        ].map(([label, current, previous, change]) => (
          <DashboardPanel key={label} className="p-5">
            <h4 className="text-sm text-slate-600">{label}</h4>
            <p className="text-3xl font-semibold mt-3 text-slate-900">{current}</p>
            <p className="text-xs text-slate-500 mt-2">
              {copy.previous}: {previous} · {copy.change}: {change}
            </p>
          </DashboardPanel>
        ))}
      </div>
      <DashboardPanel className="p-5 min-w-0">
        <h4 className="font-semibold text-slate-900">{copy.daily}</h4>
        <p className="text-xs text-slate-500 mt-1 mb-4">{copy.utc}</p>
        {!comparison.current.completed ? (
          <p className="py-10 text-sm text-slate-500">{base.noCompleted}</p>
        ) : (
          <div className="h-64 w-full" role="img" aria-label={copy.chartLabel}>
            <DailyResultsChart days={history.daily} copy={copy} />
          </div>
        )}
        <div className="flex gap-5 text-xs text-slate-600 mt-3">
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2" />
            {copy.passed}
          </span>
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-2" />
            {copy.failed}
          </span>
        </div>
        <details className="mt-4 text-xs text-slate-600">
          <summary className="cursor-pointer">{copy.dailyTable}</summary>
          <div className="overflow-x-auto max-h-64 mt-3">
            <table className="w-full text-left" aria-label={copy.dailyTable}>
              <thead>
                <tr>
                  <th className="p-2">{copy.date}</th>
                  <th className="p-2">{copy.passed}</th>
                  <th className="p-2">{copy.failed}</th>
                  <th className="p-2">{copy.success}</th>
                </tr>
              </thead>
              <tbody>
                {history.daily.map((day) => (
                  <tr key={day.date}>
                    <td className="p-2">{day.date}</td>
                    <td className="p-2">{day.passed}</td>
                    <td className="p-2">{day.failed}</td>
                    <td className="p-2">{rate(day.success_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </DashboardPanel>
      <DashboardPanel className="p-5 min-w-0">
        <h4 className="font-semibold text-slate-900">{copy.projects}</h4>
        <p className="text-xs text-slate-500 mt-1 mb-4">{copy.projectHint}</p>
        {!history.projects.length ? (
          <p className="py-5 text-sm text-slate-500">{copy.noHistory}</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="w-full text-left text-sm whitespace-nowrap"
              aria-label={copy.projects}
            >
              <thead className="text-xs text-slate-500">
                <tr>
                  {[
                    base.project,
                    copy.executions,
                    copy.success,
                    copy.previousRate,
                    copy.rateChange,
                  ].map((label) => (
                    <th className="p-3" key={label}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.projects.map((project) => (
                  <tr key={project.id}>
                    <td className="p-3">
                      <button
                        type="button"
                        className="text-violet-700 hover:underline"
                        onClick={() => onProject(project.id)}
                      >
                        {projectName(project, base)}
                      </button>
                    </td>
                    <td className="p-3">{project.current.completed}</td>
                    <td className="p-3">{rate(project.current.success_rate)}</td>
                    <td className="p-3">{rate(project.previous.success_rate)}</td>
                    <td className="p-3">{delta(project.success_rate_delta_pp, ' pp')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DashboardPanel>
      <DashboardPanel className="p-5">
        <h4 className="font-semibold text-slate-900">
          {copy.mixed}{' '}
          <span className="text-sm text-slate-500">({history.mixed_outcomes_total})</span>
        </h4>
        <p className="text-xs text-slate-500 mt-1 mb-4">{copy.mixedHint}</p>
        {!history.mixed_outcomes.length && (
          <p className="py-5 text-sm text-slate-500">{copy.noMixed}</p>
        )}
        <div className="divide-y divide-slate-100">
          {history.mixed_outcomes.map((test) => (
            <button
              type="button"
              key={`${test.type}-${test.id}`}
              disabled={Boolean(opening)}
              onClick={() => onOpen(test)}
              className="flex w-full items-center justify-between gap-3 py-3 text-left disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-violet-700">
                  {test.name}
                </span>
                <span className="text-xs text-slate-500">
                  {test.type === 'api_test' ? 'API' : 'Flow'} · {test.passed} {copy.passed} ·{' '}
                  {test.failed} {copy.failed}
                </span>
              </span>
              <span className="text-xs text-slate-500 shrink-0">{base.history} ↗</span>
            </button>
          ))}
        </div>
        {history.mixed_outcomes_total > history.mixed_outcomes.length && (
          <p className="text-xs text-slate-500 mt-3">
            {base.showing} {history.mixed_outcomes.length} {base.of} {history.mixed_outcomes_total}
          </p>
        )}
      </DashboardPanel>
    </div>
  );
}
